import React from 'react';

import type { DocumentNode } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { debounce, isEmpty, difference } from 'lodash';

import usePrevious from 'common/helpers/usePrevious';

export type UseStaleEntitiesInput<EntityResponse, QueryResponse> = {
  selectStaleEntities: (resp?: Partial<QueryResponse>) => EntityResponse[];
  selectEntityId: (entity: EntityResponse) => string;
  query: DocumentNode;
};

export type UseStaleEntitiesOutput<EntityResponse> = {
  markAsUpToDate: (entity: EntityResponse) => void;
  markAsStale: (entity: EntityResponse) => void;
};

export const SYNC_DEBOUNCE_DURATION = 5000;

export default function useStaleEntities<EntityResponse, QueryResponse>(
  props: React.PropsWithChildren<UseStaleEntitiesInput<EntityResponse, QueryResponse>>
): UseStaleEntitiesOutput<EntityResponse> {
  const [staleEntities, setStaleEntities] = React.useState<Record<string, EntityResponse>>({});
  const prevStaleEntities = usePrevious(staleEntities);

  const { selectStaleEntities, selectEntityId, query } = props;

  const client = useApolloClient();

  // removes given entities from stale entities
  const markAllAsUpToDate = React.useCallback(
    (entities: EntityResponse[]) => {
      const newStaleEntities = entities.reduce((acc, curr) => {
        const { [selectEntityId(curr)]: omit, ...otherStaleEntities } = acc;

        return otherStaleEntities;
      }, staleEntities);

      setStaleEntities(newStaleEntities);
    },
    [staleEntities, selectEntityId]
  );

  const markAsUpToDate = React.useCallback(
    (entity: EntityResponse) => markAllAsUpToDate([entity]),
    [markAllAsUpToDate]
  );

  // executes query with stale entity ids as variable
  const syncStaleEntities = React.useRef(
    debounce(async (staleEntitiesToSync: { [id: string]: EntityResponse }) => {
      try {
        const { data } = await client.query({
          query,
          fetchPolicy: 'network-only',
          variables: {
            id: Object.keys(staleEntitiesToSync),
            fetch: false,
            sync: true,
          },
        });

        selectStaleEntities(data).forEach(markAsUpToDate);
      } catch (err) {
        // just consume the error
      }
    }, SYNC_DEBOUNCE_DURATION)
  );

  // whenever stale entities are updated, call syncStaleEntities debounce function
  React.useEffect(() => {
    if (
      staleEntities !== prevStaleEntities &&
      !isEmpty(staleEntities) &&
      difference(Object.keys(staleEntities), Object.keys(prevStaleEntities || {})).length > 0
    ) {
      syncStaleEntities.current(staleEntities);
    }

    if (staleEntities !== prevStaleEntities && isEmpty(staleEntities)) {
      syncStaleEntities.current.cancel();
    }
  }, [staleEntities, prevStaleEntities]);

  // adds given entity to stale entities
  const markAsStale = (entity: EntityResponse) => {
    setStaleEntities({
      ...staleEntities,
      [selectEntityId(entity)]: entity,
    });
  };

  return { markAsStale, markAsUpToDate };
}
